import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import SiteSelector from "@/components/SiteSelector";
import ScanFAB from "@/components/ScanFAB";
import BillingGate from "@/components/BillingGate";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  LayoutDashboard, Building2, Wrench, Gauge, FileWarning, FileText,
  Users, BookOpen, Settings, Bell, LogOut, ClipboardList, ClipboardCheck, Sparkles, Activity, Inbox, ShieldCheck, ScrollText, Palette, ChevronRight, FileSpreadsheet, Users2, Siren, AlertTriangle, GraduationCap, FolderArchive, Archive, UtensilsCrossed, Rocket, Sliders, Thermometer, MonitorPlay, ListChecks, UserCheck, Truck, ClipboardEdit, Printer,
} from "lucide-react";

const NAV_BY_ROLE = {
  super_admin: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/tenants", label: "Tenants", icon: Building2 },
    { to: "/app/food-prep-config", label: "Food Prep", icon: UtensilsCrossed },
    { to: "/app/tiers", label: "Subscription Tiers", icon: Gauge },
    { to: "/app/feature-rollout", label: "Feature Rollout", icon: Rocket },
    { to: "/app/tenant-features", label: "Tenant Features", icon: Sliders },
    { to: "/app/demo-setup", label: "Demo Setup", icon: MonitorPlay },
    { to: "/app/checklist-templates", label: "Daily Tracking List", icon: ClipboardList },
    { to: "/app/cleaning-protocols", label: "Cleaning Protocols", icon: ClipboardCheck },
    { to: "/app/hot-cold-hold-config", label: "Daily Kitchen Record", icon: Thermometer },
    { to: "/app/vehicle-clean-config", label: "Daily Vehicle Cleanliness Checklist", icon: Truck },
    { to: "/app/visitor-reg-config", label: "Visitor Registration Form", icon: UserCheck },
    { to: "/app/leads", label: "Service Enquiries", icon: Inbox },
    { to: "/app/printer-labels", label: "Printer Labels", icon: Printer },
    { to: "/app/users", label: "App Staff", icon: Users },
    { to: "/app/brand-settings", label: "Brand Settings", icon: Palette },
    { to: "/app/audit-log", label: "Audit Log", icon: ScrollText },
  ],
  // Sidebar order locked in 2026-06-25 per user request: Dashboard →
  // Daily Tracking → CCP triplet → Incident → Training → Equipment →
  // Site → FSMS team → Documentation → Reports → Audit Vault → Raw Data
  // → AI Knowledge → Team → Settings. Per-role variants only DROP keys
  // the role can't see — they NEVER reorder. Touch this order with care;
  // it's the user's mental model of the product.
  owner:      ["dashboard", "critical-tasks", "daily-tracking", "food-prep", "ccps", "log", "deviations", "emergency", "training-coms", "hot-cold-hold", "equipment", "forms", "visitor-reg", "sites", "fsms-team", "documentation", "reports", "audit-vault", "raw-data", "knowledge", "users", "settings"],
  manager:    ["dashboard", "critical-tasks", "daily-tracking", "food-prep", "ccps", "log", "deviations", "emergency", "training-coms", "hot-cold-hold", "equipment", "forms", "visitor-reg", "sites", "fsms-team", "documentation", "reports", "audit-vault", "raw-data", "knowledge", "users", "settings"],
  supervisor: ["dashboard", "critical-tasks", "daily-tracking", "food-prep", "ccps", "log", "deviations", "emergency", "training-coms", "hot-cold-hold",              "forms", "visitor-reg", "fsms-team", "documentation", "reports", "audit-vault", "raw-data", "knowledge",          "settings"],
  staff:      ["critical-tasks", "daily-tracking", "food-prep",          "log",                "emergency", "hot-cold-hold",                             "forms", "fsms-team",                                                                          "knowledge", "my-readings"],
};

// Sidebar nav-key → tenant feature flag(s). If ANY listed feature is
// enabled for the tenant, the sidebar item is shown; otherwise it is
// hidden. Nav keys not in this map are not feature-gated. Applies to
// every role including owner (the Tenant Features admin is the single
// source of truth for module visibility across roles).
const NAV_FEATURE_GATE = {
  "food-prep":     ["food-prep"],
  "training-coms": ["training-coms"],
  "hot-cold-hold": ["cooking-record", "hot-cold-hold", "thawing", "metal-detector"],
  "visitor-reg":   ["visitor-registration"],
  forms:           ["forms"],
  documentation:   ["pest-control", "approved-vendor", "compliance-documents"],
};

// Nav keys that are NOT part of the per-role access-matrix (module system), so
// they must bypass matrix narrowing for non-owner roles. `critical-tasks` is a
// cross-module action hub; `hot-cold-hold` + `visitor-reg` are gated by tenant
// feature flags (which apply to every role, owner included) rather than the matrix.
const MATRIX_EXEMPT_NAV_KEYS = new Set(["critical-tasks", "hot-cold-hold", "visitor-reg", "forms"]);

const NAV_DEF = {
  dashboard: { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  "critical-tasks": { to: "/app/critical-tasks", label: "Critical Tasks", icon: ListChecks },
  log: { to: "/app/log", label: "CCP Data Submission", icon: ClipboardList },
  "my-readings": { to: "/app/my-readings", label: "My Readings", icon: ClipboardList },
  "daily-tracking": { to: "/app/daily-tracking", label: "Daily Tracking", icon: Activity },
  "food-prep": { to: "/app/food-prep", label: "Food Prep", icon: UtensilsCrossed },
  deviations: { to: "/app/deviations", label: "CCP Deviations", icon: FileWarning },
  "hot-cold-hold": { to: "/app/hot-cold-hold", label: "Daily Kitchen Record", icon: Thermometer },
  sites: { to: "/app/sites", label: "Sites", icon: Building2 },
  "fsms-team": { to: "/app/fsms-team", label: "FSMS Team", icon: Users2 },
  "training-coms": { to: "/app/training-coms", label: "Training & Coms", icon: GraduationCap },
  emergency: { to: "/app/emergency", label: "Incident Response", icon: Siren },
  equipment: { to: "/app/equipment", label: "Equipment (CCP)", icon: Wrench },
  forms: { to: "/app/forms", label: "Forms", icon: ClipboardEdit },
  "visitor-reg": { to: "/app/visitor-registration", label: "Visitor Registration", icon: UserCheck },
  ccps: { to: "/app/ccps", label: "CCP - Data Capture", icon: Gauge },
  knowledge: { to: "/app/knowledge", label: "AI Knowledge", icon: BookOpen },
  reports: { to: "/app/reports", label: "Reports", icon: FileText },
  "audit-vault": { to: "/app/audit-vault", label: "Audit Vault", icon: Archive },
  "raw-data": { to: "/app/raw-data", label: "Raw Data Entry", icon: FileSpreadsheet },
  documentation: { to: "/app/documentation", label: "Documentation", icon: FolderArchive },
  users: { to: "/app/users", label: "Team", icon: Users },
  settings: { to: "/app/settings", label: "Settings", icon: Settings },
};

export default function AppShell() {
  const { user, tenant, logout, hiddenModules, effectiveFeatures, platformProfile } = useAuth();
  const { confirm, notify } = useDialog();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [showNotif, setShowNotif] = useState(false);
  const [features, setFeatures] = useState(null);
  const [tierName, setTierName] = useState(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState(null);
  const [accessMatrix, setAccessMatrix] = useState(null);
  const [hygienePending, setHygienePending] = useState(0);
  const [cleanlinessPending, setCleanlinessPending] = useState(0);
  const [ccpCounts, setCcpCounts] = useState({ rework: 0, deviations_actionable: 0 });
  const [ccpCapture, setCcpCapture] = useState({ pending_capture: 0, pending_signoff: 0 });
  const [visitorPending, setVisitorPending] = useState(0);
  const [foodExpiryPending, setFoodExpiryPending] = useState(0);
  const [dailyMissingToday, setDailyMissingToday] = useState(0);
  const supportMode = !!user?._support_mode;
  const supportExp = user?._support_exp || null;
  const [supportRemaining, setSupportRemaining] = useState(null);

  const fetchNotif = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs({ items: data.notifications || [], unread: data.unread || 0 });
    } catch (e) { console.error("Notifications load failed:", e); }
  };

  const fetchHygienePending = async () => {
    // The badge counts every record on the Daily Tracking pages that needs
    // SOMEONE's attention: unverified (waiting sign-off), awaiting_rectification
    // (failed item needs staff fix), or awaiting_approval (supervisor must
    // approve a staff rectification). Staff see it too because the rectify
    // panels are visible to them.
    if (!user || user.role === "super_admin") {
      setHygienePending(0); setCleanlinessPending(0);
      return;
    }
    const isStaff = user.role === "staff";
    const baseQs = (template) => [
      // Staff don't see the unverified queue (they can't verify), so skip it.
      isStaff ? null
        : `/checks?pending_verification=true&template_code=${template}&days=30`,
      `/checks?awaiting_rectification=true&template_code=${template}&days=60`,
      // Approval is supervisor-only.
      isStaff ? null
        : `/checks?awaiting_approval=true&template_code=${template}&days=60`,
    ].filter(Boolean);
    try {
      const hygiene = baseQs("DAILY_HYGIENE");
      const cleanliness = baseQs("CLEAN_FACILITY");
      const all = await Promise.all([...hygiene, ...cleanliness].map((u) => api.get(u)));
      const hLen = all.slice(0, hygiene.length).reduce((s, r) => s + (r.data.checks || []).length, 0);
      const cLen = all.slice(hygiene.length).reduce((s, r) => s + (r.data.checks || []).length, 0);
      setHygienePending(hLen);
      setCleanlinessPending(cLen);
    } catch { setHygienePending(0); setCleanlinessPending(0); }
  };

  const fetchCcpCounts = async () => {
    if (!user || user.role === "super_admin") { setCcpCounts({ rework: 0, deviations_actionable: 0 }); return; }
    try {
      const { data } = await api.get("/ccp-task-counts");
      setCcpCounts({ rework: data.rework || 0, deviations_actionable: data.deviations_actionable || 0 });
    } catch { setCcpCounts({ rework: 0, deviations_actionable: 0 }); }
  };

  const fetchDailyMissingToday = async () => {
    // Count facilities whose daily hygiene / cleanliness log for TODAY has
    // not yet been captured — these show as "Not yet today" on Daily Tracking
    // and belong in the Critical Tasks queue for every role that can log them.
    if (!user || user.role === "super_admin") { setDailyMissingToday(0); return; }
    try {
      const [h, c] = await Promise.all([
        api.get("/checks/facility-summary?template_code=DAILY_HYGIENE&days=1"),
        api.get("/checks/facility-summary?template_code=CLEAN_FACILITY&days=1"),
      ]);
      const missing =
        (h.data.facilities || []).filter((f) => !f.today_logged).length +
        (c.data.facilities || []).filter((f) => !f.today_logged).length;
      setDailyMissingToday(missing);
    } catch { setDailyMissingToday(0); }
  };

  const fetchFoodExpiry = async () => {
    if (!user || user.role === "super_admin") { setFoodExpiryPending(0); return; }
    try {
      const { data } = await api.get("/food-prep/disposal-list");
      setFoodExpiryPending((data.flagged || []).length + (data.expired || []).length);
    } catch { setFoodExpiryPending(0); }
  };

  const fetchCcpCapture = async () => {
    if (!user || user.role === "super_admin") { setCcpCapture({ pending_capture: 0, pending_signoff: 0 }); return; }
    try {
      const { data } = await api.get("/ccp-capture-counts");
      setCcpCapture({ pending_capture: data.pending_capture || 0, pending_signoff: data.pending_signoff || 0 });
    } catch { setCcpCapture({ pending_capture: 0, pending_signoff: 0 }); }
  };

  const fetchVisitorPending = async () => {
    // Sidebar badge on "Visitor Registration" — every submission the caller
    // can act on (site-scoped) that's still waiting for staff attestation.
    if (!user || user.role === "super_admin" || user.role === "staff") {
      setVisitorPending(0); return;
    }
    try {
      const { data } = await api.get("/visitor-registrations/pending-count");
      setVisitorPending(data.pending || 0);
    } catch { setVisitorPending(0); }
  };

  useEffect(() => {
    fetchNotif();
    fetchHygienePending();
    fetchCcpCounts();
    fetchFoodExpiry();
    fetchCcpCapture();
    fetchVisitorPending();
    fetchDailyMissingToday();
    const t = setInterval(fetchNotif, 20000);
    const h = setInterval(fetchHygienePending, 60000);
    const c = setInterval(fetchCcpCounts, 30000);
    const f = setInterval(fetchFoodExpiry, 60000);
    const cc = setInterval(fetchCcpCapture, 60000);
    const vp = setInterval(fetchVisitorPending, 60000);
    const dm = setInterval(fetchDailyMissingToday, 60000);
    return () => { clearInterval(t); clearInterval(h); clearInterval(c); clearInterval(f); clearInterval(cc); clearInterval(vp); clearInterval(dm); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenant/features");
        setFeatures(data.features);
        setTierName(data.tier_name);
        setTrialExpiresAt(data.trial_expires_at || null);
        setAccessMatrix(data.access_matrix || null);
      } catch (e) { console.error("Tenant features load failed:", e); }
    })();
  }, [user?.id]);

  // Apply the tenant's curated brand-theme palette on first load.
  // (Resolution now lives in ThemeProvider — kept here as a stub for clarity.)


  // ---- Support Mode live countdown ----
  // Tick every second so the banner shows MM:SS remaining. When the JWT
  // expires we auto-fire exitSupportMode() so VB staff never sit on a dead
  // session and inadvertently get logged out without context.
  useEffect(() => {
    if (!supportMode || !supportExp) return;
    const tick = () => {
      const rem = Math.max(0, supportExp - Math.floor(Date.now() / 1000));
      setSupportRemaining(rem);
      if (rem <= 0) {
        clearInterval(id);
        // Inline exit so we don't depend on closure references created below.
        const adminTok = localStorage.getItem("vb_admin_token");
        if (adminTok) {
          localStorage.setItem("vb_access_token", adminTok);
          localStorage.removeItem("vb_admin_token");
          window.location.assign("/app/tenants");
        } else {
          window.location.assign("/login");
        }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [supportMode, supportExp]);

  const [demoRestoring, setDemoRestoring] = useState(false);

  if (!user || user === false) return null;

  const role = user.role;
  const FEATURE_NAV_MAP = {
    knowledge: "knowledge_base_enabled",
  };

  const items = role === "super_admin"
    ? (user?.staff_tier === "sales"
        ? NAV_BY_ROLE.super_admin.filter((it) => ["/app", "/app/tenants", "/app/tiers", "/app/feature-rollout", "/app/tenant-features", "/app/demo-setup"].includes(it.to))
        : NAV_BY_ROLE.super_admin)
    : (() => {
        let keys = NAV_BY_ROLE[role] || [];
        // Globally-hidden modules (phased rollout) are dropped for everyone.
        keys = keys.filter((k) => !(hiddenModules || []).includes(k));
        // Per-tenant feature flags: drop nav items whose feature(s) are OFF.
        // A nav item that maps to multiple features (e.g. Daily Kitchen
        // Record → 4 sub-features) stays visible if ANY of them is ON.
        const feat = effectiveFeatures || {};
        keys = keys.filter((k) => {
          const flags = NAV_FEATURE_GATE[k];
          if (!flags) return true;
          return flags.some((f) => feat[f] !== false);
        });
        // Narrow by tenant access_matrix (owner never narrowed by matrix).
        if (role !== "owner" && accessMatrix && Array.isArray(accessMatrix[role])) {
          const allowed = new Set(accessMatrix[role]);
          keys = keys.filter((k) => MATRIX_EXEMPT_NAV_KEYS.has(k) || allowed.has(k));
        }
        return keys
          .filter((k) => !features || !FEATURE_NAV_MAP[k] || features[FEATURE_NAV_MAP[k]])
          .map((k) => NAV_DEF[k])
          .filter(Boolean);   // drop unknown keys so a stale access matrix can never crash render
      })();

  const doLogout = async () => { await logout(); nav("/login"); };

  const restoreDemo = async () => {
    if (!await confirm({
      title: "Restore demo to baseline?",
      message: "This restores the demo to its saved baseline. All current demo data will be replaced.",
      danger: true,
      confirmLabel: "Restore to default",
    })) return;
    setDemoRestoring(true);
    try {
      await api.post("/demo/restore-self");
      window.location.reload();
    } catch (e) {
      await notify({ title: "Restore failed", message: e.response?.data?.detail || "Restore failed", variant: "err" });
      setDemoRestoring(false);
    }
  };

  const exitSupportMode = async () => {
    const adminTok = localStorage.getItem("vb_admin_token");
    if (!adminTok) { await logout(); nav("/login"); return; }
    localStorage.setItem("vb_access_token", adminTok);
    localStorage.removeItem("vb_admin_token");
    window.location.assign("/app/tenants");
  };

  const fmtMMSS = (sec) => {
    if (sec == null) return "--:--";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };
  const supportWarn = supportRemaining != null && supportRemaining <= 120; // pulse under 2 min

  // Trial banner state
  const trialMs = trialExpiresAt ? new Date(trialExpiresAt).getTime() - Date.now() : null;
  const trialDaysLeft = trialMs != null ? Math.ceil(trialMs / 86400000) : null;
  const isTrial = tierName?.toLowerCase() === "trial";
  const showTrialWarn = isTrial && trialDaysLeft !== null && trialDaysLeft <= 7 && trialDaysLeft >= 0;
  const trialExpired = isTrial && trialDaysLeft !== null && trialDaysLeft < 0;

  const markAll = async () => { await api.post("/notifications/read-all"); fetchNotif(); };

  // Total outstanding actions surfaced on the Critical Tasks hub. Deviations
  // are scoped like the hub: staff see rework only, supervisor+ also see
  // reviewer-actionable deviations.
  const devCount = ccpCounts.rework + (role !== "staff" ? ccpCounts.deviations_actionable : 0);
  const ccpCaptureCount = role === "staff" ? ccpCapture.pending_capture : ccpCapture.pending_signoff;
  const criticalTotal = hygienePending + cleanlinessPending + devCount + foodExpiryPending + ccpCaptureCount + dailyMissingToday;

  return (
    <div className="vb-shell" data-testid="app-shell">
      <aside className="vb-sidebar">
        <div className="vb-brand-block" data-testid="sidebar-brand">
          <div className="vb-brand-row">
            {tenant?.profile?.logo_icon_url ? (
              <img src={tenant.profile.logo_icon_url} alt="" className="vb-brand-logo" data-testid="sidebar-logo" />
            ) : tenant?.branding?.logo_url ? (
              <img src={tenant.branding.logo_url} alt="" className="vb-brand-logo" data-testid="sidebar-logo" />
            ) : null}
            <span className="vb-brand-name" data-testid="sidebar-company-name">
              {/* Priority: tenant's own legal name (Settings → Company
                  Profile) → tenant's signup name → platform's canonical
                  company name → hardcoded default. This fixes the bug
                  where entering a legal company name in Settings didn't
                  update the sidebar. */}
              {tenant?.profile?.legal_company_name?.trim()
                || tenant?.company_name
                || platformProfile?.legal_company_name?.trim()
                || "Vanguard Beacon"}
            </span>
          </div>
          {tierName && (
            <div className="vb-brand-tier" data-testid="sidebar-tier">{tierName}</div>
          )}
          {user.role !== "super_admin" ? (
            <NavLink to="/app/me" className="vb-brand-user" data-testid="sidebar-user" title="View my profile">
              <span className="name">{user.name}</span>
              <span className="dot">·</span>
              <span className="role">{user.role.replace("_", " ")}</span>
              <ChevronRight className="vb-brand-user-chev" size={14} aria-hidden="true" />
            </NavLink>
          ) : (
            <div className="vb-brand-user" data-testid="sidebar-user">
              <span className="name">{user.name}</span>
              <span className="dot">·</span>
              <span className="role">VB {(user.staff_tier || "admin").replace(/^./, c => c.toUpperCase())}</span>
            </div>
          )}
        </div>
        <nav className="vb-nav-scroll" data-testid="sidebar-nav">
          {items.map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end}
              className={({ isActive }) => `vb-nav ${isActive ? "active" : ""}`}
              data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <it.icon className="ico" size={18} />
              <span>{it.label}</span>
              {it.to === "/app/critical-tasks" && criticalTotal > 0 && (
                <span
                  className="ml-auto text-[10px] rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="nav-critical-badge"
                  title={`${criticalTotal} outstanding critical task(s)`}
                >
                  {criticalTotal}
                </span>
              )}
              {it.to === "/app/daily-tracking" && (hygienePending + cleanlinessPending) > 0 && (
                <span
                  className="ml-auto text-[10px] rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="nav-tracking-badge"
                  title={`${hygienePending} hygiene · ${cleanlinessPending} cleanliness · pending action (sign-off / rectification / approval)`}
                >
                  {hygienePending + cleanlinessPending}
                </span>
              )}
              {it.to === "/app/log" && ccpCounts.rework > 0 && (
                <span
                  className="ml-auto text-[10px] rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="nav-rework-badge"
                  title={`${ccpCounts.rework} deviation rework task(s) awaiting action`}
                >
                  {ccpCounts.rework}
                </span>
              )}
              {it.to === "/app/ccps" && ccpCapture.pending_signoff > 0 && (
                <span
                  className="ml-auto text-[10px] rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="nav-ccp-signoff-badge"
                  title={`${ccpCapture.pending_signoff} day(s) with completed CCP readings awaiting your sign-off`}
                >
                  {ccpCapture.pending_signoff}
                </span>
              )}
              {it.to === "/app/deviations" && ccpCounts.deviations_actionable > 0 && (
                <span
                  className="ml-auto text-[10px] rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="nav-deviations-badge"
                  title={`${ccpCounts.deviations_actionable} deviation(s) needing review / closure`}
                >
                  {ccpCounts.deviations_actionable}
                </span>
              )}
              {it.to === "/app/visitor-registration" && visitorPending > 0 && (
                <span
                  className="ml-auto text-[10px] rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="nav-visitor-pending-badge"
                  title={`${visitorPending} visitor entr${visitorPending === 1 ? "y" : "ies"} pending sign-off`}
                >
                  {visitorPending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="vb-sidebar-foot pt-4 border-t" style={{ borderColor: "rgba(231, 215, 200, 0.15)" }}>
          <button onClick={doLogout} className="vb-nav w-full text-left" data-testid="logout-btn">
            <LogOut className="ico" size={18} /> Sign out
          </button>
        </div>
      </aside>

      <main style={{ overflowX: "hidden", minWidth: 0 }}>
        {supportMode && (
          <div data-testid="support-mode-banner"
            className={supportWarn ? "vb-support-pulse" : ""}
            style={{
              background: "var(--vb-error)", color: "white", padding: "10px 20px",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              fontSize: 13, fontWeight: 600,
            }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {supportWarn
                ? <AlertTriangle size={14} style={{ display: "inline" }} />
                : <ShieldCheck size={14} style={{ display: "inline" }} />}
              SUPPORT MODE — you are signed in as the tenant owner. Every action is logged.
              {supportRemaining != null && (
                <span
                  data-testid="support-mode-countdown"
                  title="Session auto-expires at 00:00"
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    padding: "2px 8px",
                    background: "rgba(255,255,255,0.18)",
                    borderRadius: 6,
                    letterSpacing: 0.5,
                    marginLeft: 4,
                  }}
                >
                  {fmtMMSS(supportRemaining)} remaining
                </span>
              )}
            </span>
            <button className="vb-btn ghost" onClick={exitSupportMode} data-testid="exit-support-mode" style={{ color: "white", borderColor: "white" }}>
              Exit support mode
            </button>
          </div>
        )}
        {trialExpired && (
          <div data-testid="trial-expired-banner" style={{
            background: "var(--vb-error)", color: "white", padding: "10px 20px",
            fontSize: 13, fontWeight: 600,
          }}>
            Your trial has expired. Read-only mode is active. Contact Vanguard Beacon to upgrade.
          </div>
        )}
        {!trialExpired && showTrialWarn && (
          <div data-testid="trial-warning-banner" style={{
            background: "var(--vb-warning)", color: "white", padding: "8px 20px",
            fontSize: 13, fontWeight: 600,
          }}>
            Trial ends in {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} — contact Vanguard Beacon to keep your data and upgrade.
          </div>
        )}
        {tenant?.is_demo && (
          <div data-testid="demo-mode-banner" style={{
            background: "var(--vb-accent)", color: "white", padding: "8px 20px",
            fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 12,
          }}>
            <span className="inline-flex items-center gap-2"><MonitorPlay size={15} /> DEMO MODE — this is a sales demo workspace.</span>
            <button className="vb-btn ghost" onClick={restoreDemo} disabled={demoRestoring}
              data-testid="restore-demo-default" style={{ color: "white", borderColor: "white" }}>
              {demoRestoring ? "Restoring…" : "Restore to Default"}
            </button>
          </div>
        )}
        <div className="vb-topbar">
          <div className="flex items-center gap-3">
            <SiteSelector />
          </div>
          <div className="relative">
            <button onClick={() => { setShowNotif(!showNotif); if (!showNotif) fetchNotif(); }}
              className="vb-btn ghost relative" data-testid="notif-bell">
              <Bell size={18} />
              {notifs.unread > 0 && (
                <span className="absolute -top-1 -right-1 text-xs rounded-full px-1.5 py-0.5"
                  style={{ background: "var(--vb-error)", color: "white", minWidth: 18, textAlign: "center" }}
                  data-testid="notif-badge">
                  {notifs.unread}
                </span>
              )}
            </button>
            {showNotif && (
              <div className="absolute right-0 mt-2 w-96 vb-card p-4 z-20" style={{ maxHeight: 480, overflowY: "auto" }} data-testid="notif-panel">
                <div className="flex justify-between items-center mb-2">
                  <strong>Notifications</strong>
                  {notifs.unread > 0 && <button onClick={markAll} className="text-xs vb-link">Mark all read</button>}
                </div>
                {notifs.items.length === 0 && <p className="text-sm opacity-60">No notifications yet.</p>}
                {notifs.items.map((n) => (
                  <div key={n.id} className="py-2 border-b" style={{ opacity: n.read ? 0.55 : 1 }}>
                    <div className="flex justify-between gap-2">
                      <strong className="text-sm">{n.title}</strong>
                      <span className="vb-pill err text-[10px]">{n.severity}</span>
                    </div>
                    <p className="text-xs opacity-80 mt-1">{n.message}</p>
                    <p className="text-[10px] opacity-50 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="vb-content vb-fade-in" key={typeof window !== "undefined" ? window.location.pathname : ""}>
          <Outlet />
        </div>
      </main>
      {role !== "super_admin" && <ScanFAB />}
      <BillingGate />
    </div>
  );
}
